/*
 * The shared modal chrome every dialog inherits through the theme Modal defaults.
 * Set on the theme (not per modal) so a raw Modal, the AppModal shell, and the two
 * fullscreen modals all read the same header rhythm and title weight without any
 * call site restating it. Theme-level classNames only resolve under the real
 * buildTheme, so a bare MantineProvider in a test keeps Mantine's stock chrome —
 * which is why the AppModal test asserts on structure and content, not on these
 * styles.
 */

/* The header sits flush with the body padding and carries a hairline divider so
 * the title reads as a distinct band above the content rather than floating over
 * it. The bottom margin opens a calm gap to the first body row. */
.header {
  align-items: flex-start;
  padding-bottom: var(--mantine-spacing-sm);
  margin-bottom: var(--mantine-spacing-md);
  border-bottom: 1px solid var(--mantine-color-default-border);
}

/* The title is the one weight + size every dialog heading shares: the 600 weight
 * the rest of the chrome uses (Title is already fw:600) at a calm md size, so a
 * Manage / Share / Delete heading reads identically.
 *
 * The class is doubled to raise its specificity to (0,2,0). It lands on Mantine's
 * own <h2> AND, through the `dialog-title` Text variant, on a <Text> whose root
 * class already declares font-size and font-weight at (0,1,0) — a tie that would
 * otherwise be settled by stylesheet injection order, which is not something a
 * heading's weight should depend on.
 *
 * min-width lets the title box give up width inside the header's flex row. Without
 * it a long subject line holds its content width and widens the dialog past its
 * `size` instead of ellipsising inside it. */
.title.title {
  min-width: 0;
  font-size: var(--mantine-font-size-md);
  font-weight: 600;
  line-height: 1.3;
}

/* The header's left column: the icon accent beside the title + description stack,
 * held as ONE flex child so the header band's space-between still pushes the close
 * button to the far edge. flex + min-width are the first two links in the chain
 * that lets a long name shrink rather than shove the close button off the card. */
.heading {
  flex: 1;
  min-width: 0;
}

/* Stacks the title over its description. The third link in the same chain: a single
 * element holding its content width holds the whole line's. */
.headingText {
  min-width: 0;
}

/* The dimmed line under the dialog title, and the element the dialog's
 * aria-describedby points at. The weight is restated because a caller may pass a
 * composed node here (an activity glyph, a name, and its measurements) rather than
 * text, and it must read at body weight even though it sits in a header band beside
 * a 600 heading. It is always a SIBLING of the <h2>, never a child: the heading is
 * the dialog's accessible name and carries the title alone. */
.description {
  min-width: 0;
  font-weight: var(--mantine-font-weight-regular);
  line-height: 1.4;
}
